/**
 * LocationSystem - Game system for location and travel processing.
 *
 * Wraps LocationStore to integrate with the game's system registry.
 * Handles travel progress updates during tick loop and offline travel processing.
 */

import type { GameContext, GameSystem } from '@/registry/SystemRegistry';
import type { RootStore } from '@/stores/RootStore';

/**
 * Create a LocationSystem that manages location-based gameplay.
 *
 * The LocationSystem:
 * - Processes offline travel on initialization
 * - Updates travel progress each tick
 * - Delegates all logic to LocationStore
 *
 * @param rootStore - The root store instance
 * @returns GameSystem implementation for locations
 */
export function createLocationSystem(rootStore: RootStore): GameSystem {
  return {
    id: 'location',
    dependencies: [],

    /**
     * Initialize location system.
     *
     * Processes any travel that occurred while offline.
     *
     * @param _context - Game context (unused)
     */
    initialize(_context: GameContext): void {
      rootStore.locationStore.processOfflineTravel();
    },

    /**
     * Process tick for travel progress.
     *
     * @param _context - Game context (unused)
     * @param deltaMilliseconds - Time since last tick
     */
    tick(_context: GameContext, deltaMilliseconds: number): void {
      rootStore.locationStore.tick(deltaMilliseconds);
    },

    /**
     * Dispose location system.
     *
     * No cleanup needed - all state lives in LocationStore.
     */
    dispose(): void {
      // No cleanup needed
    },
  };
}
